// Screen: Lobby (pre-join).
// - input di sessione (room/name/token/pin/server)
// - anteprima camera + selettore dispositivi
// - level meter mic
// - "Entra": apre WSS, fa il join, ritorna lo stato per il router

import type { types as MC } from 'mediasoup-client';
import { SignalingClient } from './signaling.js';
import { icon } from './icons.js';
import {
  BackgroundEffects, EFFECTS, isWebGLAvailable, getEffectThumbnail,
  idbList, idbAdd, registerEffect,
  type EffectId, type Profile,
} from './effects.js';

export interface DevicePrefs {
  micId?: string;
  camId?: string;
  spkId?: string;
  micOn: boolean;
  camOn: boolean;
}

export interface LobbyResult {
  signaling: SignalingClient;
  joinResp: {
    peerId: string;
    role?: 'host' | 'member' | 'guest';
    waiting?: boolean;
    routerRtpCapabilities?: MC.RtpCapabilities;
    peers?: { id: string; displayName: string; role: 'host' | 'member' | 'guest' }[];
    existingProducers?: { peerId: string; producerId: string; kind: MC.MediaKind; source: 'camera'|'mic'|'screen' }[];
    waitingGuests?: { id: string; displayName: string }[];
    recording?: { id: string; startedBy: string; startedAt: number } | null;
    roomLabel?: string;
    existingFiles?: {
      id: string;
      name: string;
      size: number;
      mimeType: string;
      uploaderPeerId: string;
      uploaderDisplayName: string;
      uploadedAt: number;
    }[];
  };
  devices: { mics: MediaDeviceInfo[]; cams: MediaDeviceInfo[]; spks: MediaDeviceInfo[] };
  prefs: DevicePrefs;
  roomLabel: string;
  waiting: boolean;
  micStream: MediaStream | null;
  camStream: MediaStream | null;
  /** Token JWT usato per il join — riusato per l'upload allegati (stesso jti/exp). */
  authToken: string;
  /** ID stanza canonico (slug) come accettato dal server. */
  roomId: string;
  /** Origine HTTPS del server API, derivata dall'URL WS usato per il join.
   *  Serve per upload/download allegati: in produzione è same-origin, ma in test
   *  harness il client viene servito da un altro host. */
  apiOrigin: string;
}

function parseFragment(): { token?: string; pin?: string } {
  const frag = location.hash.replace(/^#/, '');
  if (!frag) return {};
  const out: Record<string, string> = {};
  for (const part of frag.split('&')) {
    const [k, v] = part.split('=');
    if (k && v) out[decodeURIComponent(k)] = decodeURIComponent(v);
  }
  return { token: out.t, pin: out.p };
}

function decodeTokenPreview(token: string): { room?: string; role?: string; displayName?: string } {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { room: payload.room, role: payload.role, displayName: payload.displayName };
  } catch { return {}; }
}

export async function mountLobby(root: HTMLElement): Promise<LobbyResult> {
  const initial = parseFragment();
  const tokenPreview = initial.token ? decodeTokenPreview(initial.token) : {};
  // WSS URL derivato interamente da location, senza hardcode di porte:
  // - se la pagina è servita su porta NON default (es. 9443), riusala
  // - se siamo su porta default HTTPS (443), niente porta nell'URL
  // - fallback (offline/file://): meet.ala-engineering.it senza porta (dipende dal deploy)
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  const host = location.hostname || 'meet.ala-engineering.it';
  const portPart = location.port ? `:${location.port}` : '';
  const defaultServer = `${scheme}://${host}${portPart}/ws`;
  const prefs: DevicePrefs = {
    micOn: localStorage.getItem('ala-meet.mic') !== 'false',
    camOn: localStorage.getItem('ala-meet.cam') !== 'false',
    micId: localStorage.getItem('ala-meet.micId') ?? undefined,
    camId: localStorage.getItem('ala-meet.camId') ?? undefined,
    spkId: localStorage.getItem('ala-meet.spkId') ?? undefined,
  };

  const hasToken = Boolean(initial.token && tokenPreview.room);
  const roleLabel = tokenPreview.role === 'guest' ? 'Ospite invitato'
    : tokenPreview.role === 'host' ? 'Sei l\'host'
    : tokenPreview.role === 'member' ? 'Membro' : 'Riunione';
  const roleBanner = tokenPreview.role === 'host' ? 'ENTRI COME HOST'
    : tokenPreview.role === 'guest' ? 'ENTRI COME OSPITE'
    : tokenPreview.role === 'member' ? 'ENTRI COME MEMBRO'
    : '';
  const roleClass = tokenPreview.role ? `role-${tokenPreview.role}` : '';

  root.innerHTML = `
    <header class="brand-bar">
      <a class="brand" href="/">
        <img class="brand-logo" src="/ala-logo.jpg" alt="ALA Engineering" />
        <div class="brand-meta">
          <strong>ALA Meet</strong>
          <span>Videoconferenza self-hosted</span>
        </div>
      </a>
      <div class="brand-bar-right">
        <span class="pill pill-gold">${icon('shield', 'icon icon-sm')} Riunione cifrata</span>
      </div>
    </header>
    <main class="lobby">
      <section class="preview-side">
        <h1>Prepara il tuo ingresso</h1>
        <p class="sub">Verifica l'anteprima camera e microfono, scegli i dispositivi, poi entra nella riunione.</p>

        <div class="video-card${prefs.camOn ? '' : ' cam-off'}" id="preview-card">
          <video autoplay playsinline muted></video>
          <div class="face-stand-in" id="preview-face">??</div>
          ${roleBanner ? `<div class="video-overlay-top"><span class="role-pill ${roleClass}">${roleBanner}</span></div>` : ''}
          <div class="video-overlay-bottom">
            <span class="name-pill"><span class="dot"></span><span id="preview-name">Tu</span></span>
            <span class="mic-meter">${icon('mic', 'icon icon-sm')}
              <span class="mic-bars" id="preview-bars">
                <span class="b"></span><span class="b"></span><span class="b"></span><span class="b"></span><span class="b"></span>
              </span>
            </span>
          </div>
        </div>

        <div class="preview-controls">
          <button class="toggle-pill${prefs.micOn ? '' : ' off'}" id="tg-mic">
            ${icon(prefs.micOn ? 'mic' : 'mic-off')} <span>${prefs.micOn ? 'Microfono attivo' : 'Microfono off'}</span>
          </button>
          <button class="toggle-pill${prefs.camOn ? '' : ' off'}" id="tg-cam">
            ${icon(prefs.camOn ? 'cam' : 'cam-off')} <span>${prefs.camOn ? 'Camera attiva' : 'Camera off'}</span>
          </button>
          <button class="toggle-pill" id="tg-effects" type="button" hidden>
            ${icon('settings')} <span id="tg-effects-label">Effetti</span>
          </button>
          <button class="toggle-pill" id="tg-settings" type="button" aria-controls="advanced-details">
            ${icon('settings')} <span>Impostazioni</span>
          </button>
        </div>
      </section>

      <aside class="form-side">
        <form class="form-card" id="join-form">
          <h2 class="ala-h">Entra nella riunione</h2>
          ${hasToken ? `
            <div class="role-banner ${roleClass}">
              <strong>${roleBanner}</strong>
              <small>nella riunione "<b>${escape(tokenPreview.room!)}</b>"</small>
            </div>
          ` : ''}
          <div id="lobby-error" class="lobby-error" hidden></div>

          <div class="field">
            <label for="name">Il tuo nome</label>
            <input id="name" type="text" value="${escape(tokenPreview.displayName ?? '')}" required maxlength="60" autocomplete="off" />
          </div>

          <div class="field-row">
            <div class="field">
              <label for="mic-sel">Microfono</label>
              <select id="mic-sel"></select>
            </div>
            <div class="field">
              <label for="cam-sel">Camera</label>
              <select id="cam-sel"></select>
            </div>
          </div>

          <div class="field">
            <label for="spk-sel">Altoparlante</label>
            <select id="spk-sel"></select>
          </div>

          <div class="field" id="room-row" ${hasToken ? 'hidden' : ''}>
            <label for="room">Stanza</label>
            <input id="room" type="text" value="${escape(tokenPreview.room ?? '')}" ${hasToken ? '' : 'required'} autocomplete="off" />
          </div>

          <div class="field" id="token-row" ${hasToken ? 'hidden' : ''}>
            <label for="token">Token JWT</label>
            <input id="token" type="text" value="${escape(initial.token ?? '')}" ${hasToken ? '' : 'required'} autocomplete="off" />
          </div>

          <div class="field" id="pin-row" ${tokenPreview.role === 'guest' ? '' : 'hidden'}>
            <label for="pin">PIN ospite</label>
            <input id="pin" type="text" inputmode="numeric" pattern="[0-9]{4,8}" maxlength="8" value="${escape(initial.pin ?? '')}" autocomplete="off" />
          </div>

          <details class="field" id="advanced-details" style="margin-top: 4px;">
            <summary style="cursor:pointer; color: var(--ala-muted); font-size: 0.84rem; padding: 4px 0;">Server avanzato${hasToken ? ' e credenziali' : ''}</summary>
            <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 12px;">
              ${hasToken ? `
                <div class="field">
                  <label for="token-adv">Token JWT (in URL)</label>
                  <input id="token-adv" type="text" value="${escape(initial.token ?? '')}" autocomplete="off" />
                </div>
                <div class="field">
                  <label for="room-adv">Stanza (dal token)</label>
                  <input id="room-adv" type="text" value="${escape(tokenPreview.room ?? '')}" autocomplete="off" />
                </div>
              ` : ''}
              <div class="field">
                <label for="server">Server WSS</label>
                <input id="server" type="text" value="${escape(defaultServer)}" required />
              </div>
            </div>
          </details>

          <button class="btn-primary" type="submit">Entra nella riunione ${icon('check')}</button>

          <p class="hint" style="margin-top: 14px; text-align: center; color: var(--ala-muted); font-size: 0.82rem;">
            Entrando accetti la <a href="https://www.ala-engineering.it/policy" style="color: var(--ala-navy); font-weight: 600;">policy d'uso</a> aziendale.
          </p>
        </form>
      </aside>
    </main>
    <footer class="lobby-foot">
      <span>© ALA Engineering S.r.l. — meet.ala-engineering.it</span>
      <span>Server EU · Crittografia DTLS-SRTP</span>
    </footer>
  `;

  const previewVideo = root.querySelector<HTMLVideoElement>('#preview-card video')!;
  const previewCard = root.querySelector<HTMLDivElement>('#preview-card')!;
  const previewFace = root.querySelector<HTMLDivElement>('#preview-face')!;
  const previewName = root.querySelector<HTMLSpanElement>('#preview-name')!;
  const previewBars = root.querySelector<HTMLSpanElement>('#preview-bars')!;
  const tgMic = root.querySelector<HTMLButtonElement>('#tg-mic')!;
  const tgCam = root.querySelector<HTMLButtonElement>('#tg-cam')!;
  const tgEffects = root.querySelector<HTMLButtonElement>('#tg-effects')!;
  const tgEffectsLabel = root.querySelector<HTMLElement>('#tg-effects-label')!;
  const tgSettings = root.querySelector<HTMLButtonElement>('#tg-settings')!;
  const advDetails = root.querySelector<HTMLDetailsElement>('#advanced-details')!;
  const nameInput = root.querySelector<HTMLInputElement>('#name')!;
  const roomInput = root.querySelector<HTMLInputElement>('#room')!;
  const tokenInput = root.querySelector<HTMLInputElement>('#token')!;
  const tokenAdvInput = root.querySelector<HTMLInputElement>('#token-adv');
  const roomAdvInput = root.querySelector<HTMLInputElement>('#room-adv');
  const pinInput = root.querySelector<HTMLInputElement>('#pin')!;
  const pinRow = root.querySelector<HTMLDivElement>('#pin-row')!;
  const serverInput = root.querySelector<HTMLInputElement>('#server')!;
  const micSel = root.querySelector<HTMLSelectElement>('#mic-sel')!;
  const camSel = root.querySelector<HTMLSelectElement>('#cam-sel')!;
  const spkSel = root.querySelector<HTMLSelectElement>('#spk-sel')!;
  const errBox = root.querySelector<HTMLDivElement>('#lobby-error')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('#join-form button[type=submit]')!;
  const form = root.querySelector<HTMLFormElement>('#join-form')!;

  function updatePreviewName() {
    const n = nameInput.value.trim() || 'Tu';
    previewName.textContent = n;
    previewFace.textContent = initials(n);
  }
  nameInput.addEventListener('input', updatePreviewName);
  updatePreviewName();

  function syncFromToken(token: string) {
    const claims = decodeTokenPreview(token);
    if (claims.room) roomInput.value = claims.room;
    if (claims.displayName && !nameInput.value) nameInput.value = claims.displayName;
    pinRow.hidden = claims.role !== 'guest';
    updatePreviewName();
  }
  tokenInput.addEventListener('input', () => syncFromToken(tokenInput.value.trim()));
  if (tokenAdvInput) {
    tokenAdvInput.addEventListener('input', () => {
      tokenInput.value = tokenAdvInput.value;
      syncFromToken(tokenAdvInput.value.trim());
    });
  }
  if (roomAdvInput) {
    roomAdvInput.addEventListener('input', () => { roomInput.value = roomAdvInput.value; });
  }

  tgSettings.addEventListener('click', (e) => {
    e.preventDefault();
    advDetails.open = !advDetails.open;
    tgSettings.classList.toggle('active', advDetails.open);
    if (advDetails.open) advDetails.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });

  // ── Effetti sfondo nella lobby (preview) ──
  const supportsEffects = isWebGLAvailable() && typeof HTMLCanvasElement.prototype.captureStream === 'function';
  let lobbyEffectId: EffectId = (localStorage.getItem('ala-meet.effect') as EffectId | null) ?? 'none';
  let lobbyProfile: Profile = (localStorage.getItem('ala-meet.effect.profile') as Profile | null) ?? 'auto';
  let lobbyEffects: BackgroundEffects | null = null;
  function updateEffectsLabel() {
    const meta = EFFECTS.find((e) => e.id === lobbyEffectId);
    tgEffectsLabel.textContent = lobbyEffectId === 'none' ? 'Effetti' : `Effetto: ${meta?.label ?? lobbyEffectId}`;
    tgEffects.classList.toggle('active', lobbyEffectId !== 'none');
  }
  if (supportsEffects) { tgEffects.hidden = false; updateEffectsLabel(); }
  tgEffects.addEventListener('click', (e) => { e.preventDefault(); void openLobbyEffectsModal(); });

  async function openLobbyEffectsModal() {
    const isMobile = matchMedia('(hover: none) and (pointer: coarse)').matches;
    const warn = isMobile ? '<div class="effects-warn">⚠ Su mobile gli effetti consumano CPU e batteria. Profilo "Eco" attivo per default.</div>' : '';
    const customs = await idbList();
    customs.forEach((c) => registerEffect({ id: c.id, label: c.name, description: 'Sfondo personalizzato', kind: 'image' }));
    const overlay = document.createElement('div');
    overlay.className = 'ala-modal-overlay';
    overlay.innerHTML = `
      <div class="ala-modal-card effects-modal">
        <h3 class="effects-title">Sfondo virtuale</h3>
        ${warn}
        <div class="effects-profile">
          <span class="effects-profile-label">Qualità:</span>
          <label><input type="radio" name="lobby-eff-profile" value="auto" ${lobbyProfile === 'auto' ? 'checked' : ''}> Auto</label>
          <label><input type="radio" name="lobby-eff-profile" value="eco" ${lobbyProfile === 'eco' ? 'checked' : ''}> Eco</label>
          <label><input type="radio" name="lobby-eff-profile" value="alto" ${lobbyProfile === 'alto' ? 'checked' : ''}> Alto</label>
          <span class="effects-profile-info" id="lobby-eff-info">${lobbyEffects ? `attivo: ${lobbyEffects.getResolvedProfile()}` : '—'}</span>
        </div>
        <div class="effects-grid">
          ${EFFECTS.map((eff) => {
            const thumbUrl = eff.imageUrl ?? getEffectThumbnail(eff.id);
            const style = thumbUrl ? `style="background-image:url('${thumbUrl}')"` : '';
            return `
            <button type="button" class="effect-card ${eff.id === lobbyEffectId ? 'selected' : ''}" data-effect="${eff.id}" ${eff.attribution ? `title="${escape(eff.attribution)}"` : ''}>
              <div class="effect-thumb effect-thumb-${eff.id} ${thumbUrl ? 'effect-thumb-rendered' : ''}" ${style}></div>
              <div class="effect-label">${escape(eff.label)}</div>
              <div class="effect-desc">${escape(eff.description)}</div>
            </button>`;
          }).join('')}
          <label class="effect-card effect-upload">
            <input type="file" accept="image/*" id="lobby-eff-upload" hidden>
            <div class="effect-thumb effect-thumb-upload">+</div>
            <div class="effect-label">Carica…</div>
            <div class="effect-desc">Una tua immagine (resta sul dispositivo).</div>
          </label>
        </div>
        <div class="ala-modal-actions">
          <button type="button" class="ala-modal-cancel">Chiudi</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector<HTMLButtonElement>('.ala-modal-cancel')!.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelectorAll<HTMLInputElement>('input[name=lobby-eff-profile]').forEach((r) => {
      r.addEventListener('change', async () => {
        lobbyProfile = r.value as Profile;
        localStorage.setItem('ala-meet.effect.profile', lobbyProfile);
        if (lobbyEffects) {
          await lobbyEffects.setProfile(lobbyProfile);
          const info = overlay.querySelector('#lobby-eff-info');
          if (info) info.textContent = `attivo: ${lobbyEffects.getResolvedProfile()}`;
        }
      });
    });
    overlay.querySelectorAll<HTMLButtonElement>('[data-effect]').forEach((card) => {
      card.addEventListener('click', async () => {
        const id = card.dataset.effect as EffectId;
        overlay.querySelectorAll('.effect-card').forEach((c) => c.classList.toggle('selected', c === card));
        await applyLobbyEffect(id);
      });
    });
    const upload = overlay.querySelector<HTMLInputElement>('#lobby-eff-upload');
    upload?.addEventListener('change', async () => {
      const file = upload.files?.[0];
      if (!file || !/^image\//.test(file.type)) return;
      const rec = await idbAdd(file.name, file);
      registerEffect({ id: rec.id, label: file.name, description: 'Sfondo personalizzato', kind: 'image' });
      await applyLobbyEffect(rec.id);
      close();
    });
  }
  let lobbyEffectsLock: Promise<void> = Promise.resolve();
  let lobbyEffectsSeq = 0;
  async function applyLobbyEffect(id: EffectId) {
    const seq = ++lobbyEffectsSeq;
    lobbyEffectId = id;
    localStorage.setItem('ala-meet.effect', id);
    updateEffectsLabel();
    if (!camStream) return;
    const prev = lobbyEffectsLock;
    let release: () => void = () => {};
    lobbyEffectsLock = new Promise<void>((r) => { release = r; });
    try { await prev; } catch { /* */ }
    try {
      if (seq !== lobbyEffectsSeq) return; // superato da una richiesta successiva
      if (id === 'none') {
        previewVideo.srcObject = camStream;
        return;
      }
      if (!lobbyEffects) lobbyEffects = new BackgroundEffects();
      await lobbyEffects.init(lobbyProfile);
      if (seq !== lobbyEffectsSeq) return;
      await lobbyEffects.setInputStream(camStream);
      if (seq !== lobbyEffectsSeq) return;
      await lobbyEffects.setEffect(id);
      if (seq !== lobbyEffectsSeq) return;
      const out = lobbyEffects.getOutputStream();
      const t = out.getVideoTracks()[0];
      if (!t || t.readyState !== 'live') throw new Error('output track non disponibile');
      previewVideo.srcObject = out;
    } catch (err) {
      showDeviceError(`Effetto non applicato: ${(err as Error).message}. Torno al video originale.`);
      lobbyEffectId = 'none';
      localStorage.setItem('ala-meet.effect', 'none');
      updateEffectsLabel();
      previewVideo.srcObject = camStream;
    } finally {
      release();
    }
  }

  // ── Device enumeration + preview stream ──
  let micStream: MediaStream | null = null;
  let camStream: MediaStream | null = null;
  let audioCtx: AudioContext | null = null;
  let levelRaf = 0;

  async function refreshDevices() {
    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch { /* */ }
    const mics = devices.filter((d) => d.kind === 'audioinput');
    const cams = devices.filter((d) => d.kind === 'videoinput');
    const spks = devices.filter((d) => d.kind === 'audiooutput');

    micSel.innerHTML = mics.length
      ? mics.map((d) => `<option value="${escape(d.deviceId)}" ${prefs.micId === d.deviceId ? 'selected' : ''}>${escape(d.label || 'Microfono')}</option>`).join('')
      : '<option value="">Nessun microfono rilevato</option>';
    camSel.innerHTML = cams.length
      ? cams.map((d) => `<option value="${escape(d.deviceId)}" ${prefs.camId === d.deviceId ? 'selected' : ''}>${escape(d.label || 'Camera')}</option>`).join('')
      : '<option value="">Nessuna camera rilevata</option>';
    spkSel.innerHTML = spks.length
      ? '<option value="">Predefinito di sistema</option>' + spks.map((d) => `<option value="${escape(d.deviceId)}" ${prefs.spkId === d.deviceId ? 'selected' : ''}>${escape(d.label || 'Altoparlante')}</option>`).join('')
      : '<option value="">Predefinito di sistema</option>';
    return { mics, cams, spks };
  }

  async function tryGetUserMedia(useExactDeviceIds: boolean, hdVideo: boolean): Promise<MediaStream | null> {
    const audio = prefs.micOn ? {
      deviceId: useExactDeviceIds && prefs.micId ? { exact: prefs.micId } : undefined,
      echoCancellation: true, noiseSuppression: true,
    } : false;
    const video = prefs.camOn ? {
      deviceId: useExactDeviceIds && prefs.camId ? { exact: prefs.camId } : undefined,
      width: hdVideo ? { ideal: 1280 } : { ideal: 640 },
      height: hdVideo ? { ideal: 720 } : { ideal: 480 },
      facingMode: useExactDeviceIds ? undefined : 'user',
    } : false;
    if (!audio && !video) return null;
    return navigator.mediaDevices.getUserMedia({ audio, video } as MediaStreamConstraints);
  }

  async function startPreview() {
    // chiudi le stream precedenti
    micStream?.getTracks().forEach((t) => t.stop());
    camStream?.getTracks().forEach((t) => t.stop());
    micStream = null; camStream = null;
    cancelAnimationFrame(levelRaf);
    audioCtx?.close().catch(() => {});
    audioCtx = null;

    // Fallback chain:
    //   1. exact deviceId (preferenza utente) + HD
    //   2. exact deviceId + SD (alcuni mobile non hanno 720p sulla camera scelta)
    //   3. no exact (lascia scegliere il browser) + facingMode:user + SD
    //   4. solo audio
    //   5. solo video
    const attempts: Array<{ ex: boolean; hd: boolean; label: string }> = [
      { ex: true, hd: true, label: 'exact+HD' },
      { ex: true, hd: false, label: 'exact+SD' },
      { ex: false, hd: false, label: 'no-exact+SD' },
    ];
    let s: MediaStream | null = null;
    let lastErr: unknown = null;
    for (const a of attempts) {
      try { s = await tryGetUserMedia(a.ex, a.hd); break; }
      catch (e) {
        lastErr = e;
        const name = (e as { name?: string }).name;
        console.warn(`[lobby] gUM ${a.label} failed:`, name, (e as Error).message);
        // Se il device-id memorizzato è stale: ripulisci e ritenta senza exact.
        if (name === 'OverconstrainedError' || name === 'NotFoundError') {
          if (prefs.micId) { localStorage.removeItem('ala-meet.micId'); prefs.micId = undefined; }
          if (prefs.camId) { localStorage.removeItem('ala-meet.camId'); prefs.camId = undefined; }
        }
      }
    }
    if (!s && prefs.camOn && prefs.micOn) {
      try { s = await navigator.mediaDevices.getUserMedia({ audio: true }); prefs.camOn = false; }
      catch (e) { lastErr = e; }
    }
    if (!s && prefs.camOn) {
      try { s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } }); prefs.micOn = false; }
      catch (e) { lastErr = e; }
    }
    if (!s) {
      console.warn('[lobby] tutti i tentativi gUM falliti', lastErr);
      const err = (lastErr as { name?: string; message?: string }) ?? {};
      showDeviceError(err.name === 'NotAllowedError'
        ? 'Permesso microfono/camera negato. Riapri dalle impostazioni del browser.'
        : `Camera/microfono non disponibili (${err.name ?? 'unknown'}).`);
      previewCard.classList.add('cam-off');
      return;
    }
    if (prefs.micOn) micStream = new MediaStream(s.getAudioTracks());
    if (prefs.camOn) camStream = new MediaStream(s.getVideoTracks());
    previewVideo.srcObject = camStream ?? null;
    previewCard.classList.toggle('cam-off', !prefs.camOn || !camStream);
    if (micStream && micStream.getAudioTracks().length > 0) startLevelMeter(micStream);
  }

  function showDeviceError(msg: string) {
    errBox.textContent = msg;
    errBox.hidden = false;
  }

  function startLevelMeter(stream: MediaStream) {
    audioCtx = new AudioContext();
    const src = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const bars = previewBars.querySelectorAll<HTMLSpanElement>('.b');
    const draw = () => {
      analyser.getByteFrequencyData(buf);
      let sum = 0; for (const v of buf) sum += v;
      const level = sum / buf.length / 255; // 0..1
      const lit = Math.min(5, Math.floor(level * 20));
      bars.forEach((b, i) => b.classList.toggle('on', i < lit));
      levelRaf = requestAnimationFrame(draw);
    };
    draw();
  }

  // Refresh devices dopo getUserMedia (labels visibili). Fallback graceful:
  // prima audio+video, poi solo audio, poi solo video. Non blocca se nulla c'è.
  for (const c of [{ audio: true, video: true }, { audio: true }, { video: true }]) {
    try {
      const probe = await navigator.mediaDevices.getUserMedia(c as MediaStreamConstraints);
      probe.getTracks().forEach((t) => t.stop());
      break;
    } catch { /* prova il prossimo */ }
  }
  const devs = await refreshDevices();

  // Selezione preferiti se non già impostati
  if (!prefs.micId && devs.mics[0]) prefs.micId = devs.mics[0].deviceId;
  if (!prefs.camId && devs.cams[0]) prefs.camId = devs.cams[0].deviceId;
  await startPreview();

  micSel.addEventListener('change', () => { prefs.micId = micSel.value; localStorage.setItem('ala-meet.micId', prefs.micId); void startPreview(); });
  camSel.addEventListener('change', () => { prefs.camId = camSel.value; localStorage.setItem('ala-meet.camId', prefs.camId); void startPreview(); });
  spkSel.addEventListener('change', () => {
    prefs.spkId = spkSel.value || undefined;
    if (prefs.spkId) localStorage.setItem('ala-meet.spkId', prefs.spkId);
    else localStorage.removeItem('ala-meet.spkId');
  });

  tgMic.addEventListener('click', async () => {
    prefs.micOn = !prefs.micOn;
    localStorage.setItem('ala-meet.mic', String(prefs.micOn));
    tgMic.classList.toggle('off', !prefs.micOn);
    tgMic.innerHTML = `${icon(prefs.micOn ? 'mic' : 'mic-off')} <span>${prefs.micOn ? 'Microfono attivo' : 'Microfono off'}</span>`;
    await startPreview();
  });
  tgCam.addEventListener('click', async () => {
    prefs.camOn = !prefs.camOn;
    localStorage.setItem('ala-meet.cam', String(prefs.camOn));
    tgCam.classList.toggle('off', !prefs.camOn);
    tgCam.innerHTML = `${icon(prefs.camOn ? 'cam' : 'cam-off')} <span>${prefs.camOn ? 'Camera attiva' : 'Camera off'}</span>`;
    await startPreview();
  });

  // ── Submit ──
  form.dataset.ready = '1';
  return new Promise<LobbyResult>((resolve) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      submitBtn.disabled = true; errBox.hidden = true;
      try {
        const roomId = roomInput.value.trim();
        const token = tokenInput.value.trim();
        const pin = pinInput.value.trim() || undefined;
        const serverUrl = serverInput.value.trim();
        if (!roomId || !token) throw new Error('Compila stanza e token.');

        const sig = new SignalingClient(serverUrl);
        await sig.awaitReady();

        // join
        const joinResp = await sig.request<LobbyResult['joinResp']>({
          type: 'join', roomId, token, pin,
          rtpCapabilities: {} as MC.RtpCapabilities,
        });
        // INVARIANTE SICUREZZA lato client: se il token decodificato è guest
        // ma il server NON ci ha messo in waiting, è una regressione critica.
        // Blocchiamo prima di entrare in stanza e logghiamo per i diagnostici.
        const tp = decodeTokenPreview(token);
        if (tp.role === 'guest' && !joinResp.waiting) {
          try { sig.close(); } catch { /* */ }
          throw new Error('SECURITY:guest_bypassed_waiting (server contract violation, jti=' + (joinResp.peerId ?? 'n/d') + ')');
        }
        const roomLabel = joinResp.roomLabel ?? roomId;
        // se sono guest sotto waiting: il joinResp.waiting === true
        cancelAnimationFrame(levelRaf);
        audioCtx?.close().catch(() => {});
        // Dispose lobby effects: i frame del canvas non servono più dopo l'ingresso;
        // il room.ts crea una nuova istanza BackgroundEffects sul producer.
        try { lobbyEffects?.dispose(); } catch { /* */ }
        lobbyEffects = null;

        // Deriva apiOrigin dall'URL WS (wss://host:port → https://host:port).
        const apiOrigin = (() => {
          try {
            const u = new URL(serverUrl);
            return `${u.protocol === 'wss:' ? 'https:' : 'http:'}//${u.host}`;
          } catch { return location.origin; }
        })();
        resolve({
          signaling: sig, joinResp,
          devices: { mics: devs.mics, cams: devs.cams, spks: devs.spks },
          prefs, roomLabel,
          waiting: Boolean(joinResp.waiting),
          micStream, camStream,
          authToken: token, roomId, apiOrigin,
        });
      } catch (err) {
        errBox.textContent = `Errore: ${(err as Error).message}`;
        errBox.hidden = false;
        submitBtn.disabled = false;
      }
    });
  });
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '??';
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
